import { HorseFilters, CompetitionSearchFilters } from '../types/api';

export type ViewMode =
  | 'all'
  | 'collection'
  | 'media'
  | 'horse-detail'
  | 'media-detail'
  | 'account-setting'
  | 'competitions'
  | 'competition-detail'
  | 'event-detail'
  | 'inbox'
  | 'inbox-detail';

function parseFiltersFromParams(params: URLSearchParams): HorseFilters {
  const filters: HorseFilters = {};

  if (params.get('name')) filters.name = params.get('name')!;
  if (params.get('fei_id')) filters.fei_id = params.get('fei_id')!;
  if (params.get('birth_year_min')) filters.birth_year_min = parseInt(params.get('birth_year_min')!);
  if (params.get('birth_year_max')) filters.birth_year_max = parseInt(params.get('birth_year_max')!);
  if (params.get('show_level_min')) filters.show_level_min = parseInt(params.get('show_level_min')!);
  if (params.get('show_level_max')) filters.show_level_max = parseInt(params.get('show_level_max')!);
  if (params.get('sex')) filters.sex = params.get('sex')!.split(',').filter(Boolean);
  if (params.get('countries')) filters.countries = params.get('countries')!.split(',').filter(Boolean);
  if (params.get('breed')) filters.breed = params.get('breed')!;
  if (params.get('sire')) filters.sire = params.get('sire')!;
  if (params.get('dam')) filters.dam = params.get('dam')!;
  if (params.get('in_collection')) {
    filters.in_collection = params.get('in_collection') === 'true';
  }
  if (params.get('is_private')) {
    filters.is_private = params.get('is_private') === 'true';
  }
  if (params.get('min_competitions')) filters.min_competitions = parseInt(params.get('min_competitions')!);
  if (params.get('max_competitions')) filters.max_competitions = parseInt(params.get('max_competitions')!);
  if (params.get('min_prize_eur')) filters.min_prize_eur = parseFloat(params.get('min_prize_eur')!);
  if (params.get('min_prize_gbp')) filters.min_prize_gbp = parseFloat(params.get('min_prize_gbp')!);
  if (params.get('min_prize_usd')) filters.min_prize_usd = parseFloat(params.get('min_prize_usd')!);
  if (params.get('min_prize_chf')) filters.min_prize_chf = parseFloat(params.get('min_prize_chf')!);
  if (params.get('min_prize_aud')) filters.min_prize_aud = parseFloat(params.get('min_prize_aud')!);
  if (params.get('min_prize_cad')) filters.min_prize_cad = parseFloat(params.get('min_prize_cad')!);
  if (params.get('height_cm_min')) filters.height_cm_min = parseInt(params.get('height_cm_min')!);
  if (params.get('height_cm_max')) filters.height_cm_max = parseInt(params.get('height_cm_max')!);
  if (params.has('is_pony')) filters.is_pony = params.get('is_pony') === 'true';
  if (params.has('has_videos')) filters.has_videos = params.get('has_videos') === 'true';
  if (params.get('last_result_within') && params.get('last_result_unit')) {
    filters.last_result_within = parseInt(params.get('last_result_within')!);
    filters.last_result_unit = params.get('last_result_unit')! as 'days' | 'months' | 'years';
  }

  return filters;
}

function parseCompetitionFiltersFromParams(params: URLSearchParams): CompetitionSearchFilters {
  const filters: CompetitionSearchFilters = {};
  if (params.get('comp_name')) filters.name = params.get('comp_name')!;
  if (params.get('comp_event_name')) filters.competition_name = params.get('comp_event_name')!;
  if (params.get('comp_location')) filters.location = params.get('comp_location')!;
  if (params.get('comp_date_from')) filters.date_from = params.get('comp_date_from')!;
  if (params.get('comp_date_to')) filters.date_to = params.get('comp_date_to')!;
  if (params.get('comp_height_min')) filters.height_cm_min = parseInt(params.get('comp_height_min')!);
  if (params.get('comp_height_max')) filters.height_cm_max = parseInt(params.get('comp_height_max')!);
  if (params.has('comp_is_pony_class')) filters.is_pony_class = params.get('comp_is_pony_class') === 'true';
  return filters;
}

export const urlParams = {
  // Parse filters from URL query parameters
  getFiltersFromUrl(): HorseFilters {
    return parseFiltersFromParams(new URLSearchParams(window.location.search));
  },

  // Parse filters from an arbitrary search/query string (e.g. from a saved URI)
  parseFiltersFromSearchString(search: string): HorseFilters {
    const qIdx = search.indexOf('?');
    const qs = qIdx >= 0 ? search.substring(qIdx + 1) : search;
    return parseFiltersFromParams(new URLSearchParams(qs));
  },

  // Get view mode from URL (path-based for detail routes, query-based for others)
  getViewModeFromUrl(): ViewMode {
    const pathname = window.location.pathname;
    
    // Check for path-based routing
    if (pathname.endsWith('/media')) {
      return 'media';
    }
    
    // Check for account settings route (/account_setting)
    if (pathname.endsWith('/account_setting')) {
      return 'account-setting';
    }
    
    // Check for media detail route (/media/{id})
    if (pathname.match(/^\/media\/[^/]+$/)) {
      return 'media-detail';
    }
    
    // Check for horse detail route (/horse/{id})
    if (pathname.match(/^\/horse\/[^/]+$/)) {
      return 'horse-detail';
    }

    // Check for event detail route (/event/{id})
    if (pathname.match(/^\/event\/[^/]+$/)) {
      return 'event-detail';
    }

    // Check for inbox detail route (/inbox/{id})
    if (pathname.match(/^\/inbox\/[^/]+$/)) {
      return 'inbox-detail';
    }

    // Check for inbox route (/inbox)
    if (pathname === '/inbox') {
      return 'inbox';
    }

    // Check for class detail route (/classes/{id})
    if (pathname.match(/^\/classes\/[^/]+$/)) {
      return 'competition-detail';
    }
    
    // Fall back to query parameters for collection/all/competitions views
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    if (view === 'collection') return 'collection';
    if (view === 'competitions') return 'competitions';
    return 'all';
  },

  // Get horse ID from URL path (for horse detail view)
  getHorseIdFromUrl(): string | null {
    const pathname = window.location.pathname;
    const match = pathname.match(/^\/horse\/([^/]+)$/);
    return match ? match[1] : null;
  },

  // Get media ID from URL path (for media detail view)
  getMediaIdFromUrl(): string | null {
    const pathname = window.location.pathname;
    const match = pathname.match(/^\/media\/([^/]+)$/);
    return match ? match[1] : null;
  },

  getCompetitionIdFromUrl(): string | null {
    const pathname = window.location.pathname;
    const match = pathname.match(/^\/classes\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  },

  getEventIdFromUrl(): string | null {
    const pathname = window.location.pathname;
    const match = pathname.match(/^\/event\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  },

  getInboxMessageIdFromUrl(): string | null {
    const pathname = window.location.pathname;
    const match = pathname.match(/^\/inbox\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  },

  // Get selected profile ID from URL query parameter.
  // Returns undefined (profile list landing), null ("All Horses"), or a profile UUID string.
  getProfileIdFromUrl(): string | null | undefined {
    const params = new URLSearchParams(window.location.search);
    const profile = params.get('profile');
    if (profile === null) return undefined;
    if (profile === 'all') return null;
    return profile;
  },

  // Get selected status filter from URL query parameter.
  // Returns undefined (no status filter) or a status string (e.g. 'watch', 'skip', 'own', 'sold').
  getStatusFromUrl(): string | undefined {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    return status ?? undefined;
  },

  // Simple navigation functions using browser history API
  
  // Navigate to horse detail view
  navigateToHorseDetail(horseId: string): void {
    window.history.pushState({}, '', `/horse/${horseId}`);
    window.dispatchEvent(new CustomEvent('urlchange'));
  },

  // Navigate to media detail view
  navigateToMediaDetail(mediaId: string): void {
    window.history.pushState({}, '', `/media/${mediaId}`);
    window.dispatchEvent(new CustomEvent('urlchange'));
  },

  navigateToInbox(): void {
    window.history.pushState({}, '', '/inbox');
    window.dispatchEvent(new CustomEvent('urlchange'));
  },

  navigateToInboxMessage(messageId: string): void {
    window.history.pushState({}, '', `/inbox/${encodeURIComponent(messageId)}`);
    window.dispatchEvent(new CustomEvent('urlchange'));
  },

  // Navigate back (uses browser's natural back functionality)
  navigateBack(): void {
    window.history.back();
  },

  getCompetitionFiltersFromUrl(): CompetitionSearchFilters {
    return parseCompetitionFiltersFromParams(new URLSearchParams(window.location.search));
  },

  updateUrlWithCompetitionFilters(filters: CompetitionSearchFilters): void {
    const params = new URLSearchParams();
    params.set('view', 'competitions');
    if (filters.name) params.set('comp_name', filters.name);
    if (filters.competition_name) params.set('comp_event_name', filters.competition_name);
    if (filters.location) params.set('comp_location', filters.location);
    if (filters.date_from) params.set('comp_date_from', filters.date_from);
    if (filters.date_to) params.set('comp_date_to', filters.date_to);
    if (filters.height_cm_min !== undefined) params.set('comp_height_min', filters.height_cm_min.toString());
    if (filters.height_cm_max !== undefined) params.set('comp_height_max', filters.height_cm_max.toString());
    if (filters.is_pony_class !== undefined) params.set('comp_is_pony_class', filters.is_pony_class.toString());
    window.history.pushState({}, '', `/?${params.toString()}`);
    window.dispatchEvent(new CustomEvent('urlchange'));
  },

  navigateToCompetitions(): void {
    window.history.pushState({}, '', '/?view=competitions');
    window.dispatchEvent(new CustomEvent('urlchange'));
  },

  navigateToCompetitionDetail(classId: string): void {
    window.history.pushState({}, '', `/classes/${encodeURIComponent(classId)}`);
    window.dispatchEvent(new CustomEvent('urlchange'));
  },

  navigateToEventDetail(competitionId: string): void {
    window.history.pushState({}, '', `/event/${encodeURIComponent(competitionId)}`);
    window.dispatchEvent(new CustomEvent('urlchange'));
  },

  // Update URL with new filters without page reload
  updateUrlWithFilters(filters: HorseFilters): void {
    const params = new URLSearchParams(window.location.search);
    
    // Clear existing filter params
    params.delete('name');
    params.delete('fei_id');
    params.delete('birth_year_min');
    params.delete('birth_year_max');
    params.delete('show_level_min');
    params.delete('show_level_max');
    params.delete('sex');
    params.delete('countries');
    params.delete('breed');
    params.delete('sire');
    params.delete('dam');
    params.delete('in_collection');
    params.delete('is_private');
    params.delete('min_competitions');
    params.delete('max_competitions');
    params.delete('min_prize_eur');
    params.delete('min_prize_gbp');
    params.delete('min_prize_usd');
    params.delete('min_prize_chf');
    params.delete('min_prize_aud');
    params.delete('min_prize_cad');
    params.delete('height_cm_min');
    params.delete('height_cm_max');
    params.delete('is_pony');
    params.delete('has_videos');
    params.delete('last_result_within');
    params.delete('last_result_unit');

    // Add new filter params
    if (filters.name) params.set('name', filters.name);
    if (filters.fei_id) params.set('fei_id', filters.fei_id);
    if (filters.birth_year_min !== undefined) params.set('birth_year_min', filters.birth_year_min.toString());
    if (filters.birth_year_max !== undefined) params.set('birth_year_max', filters.birth_year_max.toString());
    if (filters.show_level_min !== undefined) params.set('show_level_min', filters.show_level_min.toString());
    if (filters.show_level_max !== undefined) params.set('show_level_max', filters.show_level_max.toString());
    if (filters.sex?.length) params.set('sex', filters.sex.join(','));
    if (filters.countries?.length) params.set('countries', filters.countries.join(','));
    if (filters.breed) params.set('breed', filters.breed);
    if (filters.sire) params.set('sire', filters.sire);
    if (filters.dam) params.set('dam', filters.dam);
    if (filters.in_collection !== undefined) {
      params.set('in_collection', filters.in_collection.toString());
    }
    if (filters.is_private !== undefined) {
      params.set('is_private', filters.is_private.toString());
    }
    if (filters.min_competitions !== undefined) params.set('min_competitions', filters.min_competitions.toString());
    if (filters.max_competitions !== undefined) params.set('max_competitions', filters.max_competitions.toString());
    if (filters.min_prize_eur !== undefined) params.set('min_prize_eur', filters.min_prize_eur.toString());
    if (filters.min_prize_gbp !== undefined) params.set('min_prize_gbp', filters.min_prize_gbp.toString());
    if (filters.min_prize_usd !== undefined) params.set('min_prize_usd', filters.min_prize_usd.toString());
    if (filters.min_prize_chf !== undefined) params.set('min_prize_chf', filters.min_prize_chf.toString());
    if (filters.min_prize_aud !== undefined) params.set('min_prize_aud', filters.min_prize_aud.toString());
    if (filters.min_prize_cad !== undefined) params.set('min_prize_cad', filters.min_prize_cad.toString());
    if (filters.height_cm_min !== undefined) params.set('height_cm_min', filters.height_cm_min.toString());
    if (filters.height_cm_max !== undefined) params.set('height_cm_max', filters.height_cm_max.toString());
    if (filters.is_pony !== undefined) params.set('is_pony', filters.is_pony.toString());
    if (filters.has_videos !== undefined) params.set('has_videos', filters.has_videos.toString());
    if (filters.last_result_within !== undefined && filters.last_result_unit) {
      params.set('last_result_within', filters.last_result_within.toString());
      params.set('last_result_unit', filters.last_result_unit);
    }

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.pushState({}, '', newUrl);
  },

  updateUrlWithViewMode(viewMode: ViewMode, profileId?: string | null | undefined): void {
    if (viewMode === 'media') {
      window.history.pushState({}, '', '/media');
    } else if (viewMode === 'inbox') {
      window.history.pushState({}, '', '/inbox');
    } else if (viewMode === 'competitions') {
      window.history.pushState({}, '', '/?view=competitions');
    } else if (viewMode === 'horse-detail' || viewMode === 'media-detail' || viewMode === 'competition-detail' || viewMode === 'event-detail' || viewMode === 'inbox-detail') {
      console.warn('Use detail navigation helpers instead');
      return;
    } else {
      if (viewMode === 'collection') {
        const params = new URLSearchParams();
        params.set('view', 'collection');
        if (profileId === null) {
          params.set('profile', 'all');
        } else if (typeof profileId === 'string') {
          params.set('profile', profileId);
        }
        window.history.pushState({}, '', `/?${params.toString()}`);
      } else {
        const params = new URLSearchParams(window.location.search);
        params.delete('view');
        params.delete('profile');
        const newUrl = params.toString() ? `/?${params.toString()}` : '/';
        window.history.pushState({}, '', newUrl);
      }
    }
  },

  // Update URL with both filters and view mode (simplified).
  // profileId: undefined = profile list landing, null = All Horses, string = specific profile
  // statusFilter: undefined = no status filter, string = specific status (e.g. 'watch', 'skip')
  updateUrlWithFiltersAndView(filters: HorseFilters, viewMode: ViewMode, profileId?: string | null | undefined, statusFilter?: string): void {
    if (viewMode === 'media') {
      // Media view doesn't use filters
      window.history.pushState({}, '', '/media');
    } else if (viewMode === 'inbox') {
      window.history.pushState({}, '', '/inbox');
    } else if (viewMode === 'horse-detail' || viewMode === 'media-detail' || viewMode === 'inbox-detail') {
      console.warn('Use detail navigation helpers instead');
      return;
    } else {
      // Use query parameters for collection/all views with filters
      const params = new URLSearchParams();
      
      // Add filters
      if (filters.name) params.set('name', filters.name);
      if (filters.fei_id) params.set('fei_id', filters.fei_id);
      if (filters.birth_year_min !== undefined) params.set('birth_year_min', filters.birth_year_min.toString());
      if (filters.birth_year_max !== undefined) params.set('birth_year_max', filters.birth_year_max.toString());
      if (filters.show_level_min !== undefined) params.set('show_level_min', filters.show_level_min.toString());
      if (filters.show_level_max !== undefined) params.set('show_level_max', filters.show_level_max.toString());
      if (filters.sex?.length) params.set('sex', filters.sex.join(','));
      if (filters.countries?.length) params.set('countries', filters.countries.join(','));
      if (filters.breed) params.set('breed', filters.breed);
      if (filters.sire) params.set('sire', filters.sire);
      if (filters.dam) params.set('dam', filters.dam);
      if (filters.in_collection !== undefined) {
        params.set('in_collection', filters.in_collection.toString());
      }
      if (filters.is_private !== undefined) {
        params.set('is_private', filters.is_private.toString());
      }
      if (filters.min_competitions !== undefined) params.set('min_competitions', filters.min_competitions.toString());
      if (filters.max_competitions !== undefined) params.set('max_competitions', filters.max_competitions.toString());
      if (filters.min_prize_eur !== undefined) params.set('min_prize_eur', filters.min_prize_eur.toString());
      if (filters.min_prize_gbp !== undefined) params.set('min_prize_gbp', filters.min_prize_gbp.toString());
      if (filters.min_prize_usd !== undefined) params.set('min_prize_usd', filters.min_prize_usd.toString());
      if (filters.min_prize_chf !== undefined) params.set('min_prize_chf', filters.min_prize_chf.toString());
      if (filters.min_prize_aud !== undefined) params.set('min_prize_aud', filters.min_prize_aud.toString());
      if (filters.min_prize_cad !== undefined) params.set('min_prize_cad', filters.min_prize_cad.toString());
      if (filters.height_cm_min !== undefined) params.set('height_cm_min', filters.height_cm_min.toString());
      if (filters.height_cm_max !== undefined) params.set('height_cm_max', filters.height_cm_max.toString());
      if (filters.is_pony !== undefined) params.set('is_pony', filters.is_pony.toString());
      if (filters.has_videos !== undefined) params.set('has_videos', filters.has_videos.toString());
      if (filters.last_result_within !== undefined && filters.last_result_unit) {
        params.set('last_result_within', filters.last_result_within.toString());
        params.set('last_result_unit', filters.last_result_unit);
      }

      // Add view mode
      if (viewMode === 'collection') {
        params.set('view', 'collection');
        if (profileId === null) {
          params.set('profile', 'all');
        } else if (typeof profileId === 'string') {
          params.set('profile', profileId);
        }
        if (statusFilter) {
          params.set('status', statusFilter);
        }
      }
      
      const newUrl = params.toString() ? `/?${params.toString()}` : '/';
      window.history.pushState({}, '', newUrl);
    }
  },

  // Remove a specific filter from URL
  removeFilterFromUrl(filterKey: keyof HorseFilters): void {
    const params = new URLSearchParams(window.location.search);
    params.delete(filterKey);
    
    const newUrl = params.toString() 
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    
    window.history.pushState({}, '', newUrl);
  },

  // Clear all filters from URL (but keep view mode)
  clearAllFilters(): void {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    
    const newUrl = view ? `${window.location.pathname}?view=${view}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  },

  // Reset app to default state (home page with no filters)
  resetToHome(): void {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new CustomEvent('urlchange'));
  },

  // Navigate to account settings view
  navigateToAccountSettings(): void {
    window.history.pushState({}, '', '/account_setting');
    window.dispatchEvent(new CustomEvent('urlchange'));
  }
}; 